import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, KeyRound } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';

/**
 * /reset-password
 *
 * Supabase mode: when the user clicks the reset link in the email,
 * Supabase signs them in transiently and emits a `PASSWORD_RECOVERY`
 * event — our AuthContext picks it up and the user lands here already
 * authenticated. We then call `updatePassword(newPassword)`.
 *
 * Mock mode: the user lands here manually (no email is sent). They
 * type their email + new password; we verify a pending reset request
 * exists for that email and update directly.
 */
const ResetPassword = () => {
  const { user, loading, authMode, updatePassword, completeMockPasswordReset } = useAuth();
  const navigate = useNavigate();

  const isMock = authMode === 'mock';

  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState('');

  // In Supabase mode, if the user isn't authenticated and bootstrap is
  // done, they probably opened this URL without a valid reset token.
  useEffect(() => {
    if (!isMock && !loading && !user) {
      // Don't redirect immediately — give Supabase a moment to fire
      // the PASSWORD_RECOVERY event (auth state may not yet be set).
      const id = setTimeout(() => {
        if (!user) {
          // user state didn't materialise; the link is likely expired.
        }
      }, 1500);
      return () => clearTimeout(id);
    }
  }, [isMock, loading, user]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      if (isMock) {
        if (!email) { setError('Enter the email on the account.'); setSubmitting(false); return; }
        await completeMockPasswordReset(email, password);
      } else {
        await updatePassword(password);
      }
      toast.success('Password updated.');
      navigate('/saved', { replace: true });
    } catch (err) {
      setError(err?.message || 'Could not update password. The reset link may be expired.');
    } finally {
      setSubmitting(false);
    }
  };

  const ic = 'giwahs-auth-input mt-2';

  return (
    <div data-testid="reset-password-page" className="surface-obsidian">
      <SEO title="Set a new password — GIWAHS" description="Choose a new password for your GIWAHS buyer account." noindex />
      <section className="border-b border-hairline">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:grid-cols-12 md:px-8 md:py-24">
          <div className="md:col-span-5">
            <span className="label-overline text-gold">/ Buyer Account</span>
            <h1 className="mt-4 font-display tracking-tighter text-platinum" style={{ fontWeight: 700, fontSize: 'clamp(2.25rem, 5vw, 4.5rem)', lineHeight: 0.95 }}>
              New password.
            </h1>
            <p className="mt-5 max-w-md text-platinum-2">
              Choose a new password to regain access to your buyer account. Minimum 6 characters.
            </p>
            <div className="mt-8 border-t border-hairline pt-5 text-sm text-muted-warm">
              <Link to="/login" data-testid="reset-to-login-link" className="underline underline-offset-4 text-platinum-2 hover:text-gold transition-colors">Back to sign in</Link>
            </div>
          </div>

          <div className="md:col-span-7">
            <form data-testid="reset-form" onSubmit={submit} className="giwahs-auth-panel space-y-5 p-6 md:p-10">
              {isMock && (
                <div>
                  <label className="label-overline text-gold">Email</label>
                  <input
                    data-testid="reset-email-input"
                    type="email"
                    autoComplete="email"
                    className={ic}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                  />
                  <p className="mt-1 text-xs text-muted-warm">Mock mode only — enter the email you requested the reset for.</p>
                </div>
              )}
              <div>
                <label className="label-overline text-gold">New password</label>
                <input
                  data-testid="reset-password-input"
                  type="password"
                  autoComplete="new-password"
                  className={ic}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="label-overline text-gold">Confirm new password</label>
                <input
                  data-testid="reset-confirm-input"
                  type="password"
                  autoComplete="new-password"
                  className={ic}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={submitting}
                />
              </div>

              {error && (
                <div data-testid="reset-error" role="alert" aria-live="polite" className="giwahs-auth-error px-3 py-2 text-sm">{error}</div>
              )}

              <button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={submitting}
                className="btn-giwahs btn-giwahs-primary w-full justify-center"
              >
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Updating…</> : <><KeyRound size={14} strokeWidth={1.5} /> Update password</>}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResetPassword;
